import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { useNavigate } from 'react-router-dom';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import UserProfile from '../../components/User';
import { getVizTokens } from '../analytics/vizTokens';
import {
    STATE, groupDigits, isImpossible, looksWrong, read, unitChoices,
} from './expression';
import CountStage from './CountStage';
import {
    STORAGE_AVAILABLE, businessDate, clearDraft, dropLegacyCookie, loadDraft,
    saveDraft, savedAgo,
} from './countDraft';

/**
 * The daily stock count.
 *
 * 104 items, on a phone, standing in a working kitchen, one hand busy. The
 * shape of the screen follows from three facts about that:
 *
 * 1. The count is a walk. Items arrive in display_index order, which is the
 *    route past the shelves, so the list is never re-sorted or grouped. "Hide
 *    counted" is the only reordering, and it only ever removes rows.
 *
 * 2. Interruption is normal, not exceptional. Two separate safety nets:
 *    the draft on the phone (see countDraft.js, which replaces a cookie that
 *    silently stopped accepting entries at item 33 of 104), and Send, which
 *    works at any point. The server sets stock absolutely, so sending half a
 *    count is a real save, not a half-write - and the items sent drop off the
 *    list. If the phone dies at item 70, the first 69 are already in the books.
 *
 * 3. A wrong number here is permanent. This field overwrites stock outright and
 *    feeds the margin calculation, and 17% of this business's bill lines have
 *    already been found physically impossible. So a big jump warns, and a
 *    physically impossible figure blocks.
 *
 * Deliberately absent: any way to accept the last count without typing it. No
 * prefilled fields, no "same as yesterday" button. A stock count exists to
 * find out that stock changed; making the un-counted answer the cheapest tap
 * would manufacture data saying nothing moved, and it would be fastest exactly
 * when the person is most rushed. The previous figure is shown, and can only be
 * read.
 */

const SEARCH_MIN_ITEMS = 12;

function CountRow(props) {
    const { item, raw, unit, onChange, onUnit, tokens, isLast } = props;
    const [focused, setFocused] = useState(false);
    const units = unitChoices(item.measurement_unit);
    const active = units.find((u) => u.label === unit) || units[0];

    const reading = read(raw);
    // Always the figure in the unit the item is stored in - what gets sent.
    const value = reading.state === STATE.OK
        ? Math.round(reading.value * active.factor) : null;
    const impossible = isImpossible(value, item.measurement_unit);
    const odd = impossible ? null : looksWrong(value, item.current_inventory);
    const counted = reading.state === STATE.OK;
    const rounded = reading.state === STATE.OK && reading.value !== value;

    // Pieces are 7 of 104 items. Reading one as grams is a thousand-fold error
    // into a field that overwrites stock, so the unit is stated, not whispered.
    const isPieces = /^(p|piece|pieces|peices)$/i.test(item.measurement_unit || '');

    return (
        <>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
                backgroundColor: counted ? 'action.hover' : 'transparent',
                transition: 'background-color 120ms',
            }}>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {item.item_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        {Number(item.current_inventory) > 0
                            ? `last ${groupDigits(item.current_inventory)} ${item.measurement_unit}`
                            : 'no previous count'}
                    </Typography>
                </Box>

                <TextField
                    size="small"
                    value={raw || ''}
                    onChange={(event) => onChange(item.id, event.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    error={Boolean(impossible) || reading.state === STATE.INVALID}
                    sx={{ width: 132, flexShrink: 0 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {/* Tappable where a second unit exists, so the
                                    list offers the same choice the keypad does. */}
                                <Typography
                                    component={units.length > 1 ? 'button' : 'span'}
                                    type={units.length > 1 ? 'button' : undefined}
                                    onClick={units.length > 1 ? () => {
                                        const other = units.find(
                                            (u) => u.label !== active.label);
                                        if (other) onUnit(item.id, other.label);
                                    } : undefined}
                                    aria-label={units.length > 1
                                        ? `Unit for ${item.item_name}, currently ${active.label}`
                                        : undefined}
                                    variant="caption"
                                    sx={{
                                        border: 0, background: 'none', p: 0, font: 'inherit',
                                        cursor: units.length > 1 ? 'pointer' : 'default',
                                        fontWeight: (isPieces || units.length > 1) ? 700 : 400,
                                        color: units.length > 1 ? 'primary.main'
                                            : isPieces ? tokens.critical : 'inherit',
                                    }}>
                                    {active.label}
                                </Typography>
                            </InputAdornment>
                        ),
                    }}
                    slotProps={{
                        htmlInput: {
                            // One declaration, not the two contradictory ones this
                            // replaced - the loser of which gave iPhones a full
                            // letter keyboard for a number field.
                            inputMode: 'decimal',
                            autoComplete: 'off',
                            'aria-label': `${item.item_name}, quantity in ${item.measurement_unit}`,
                            style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
                        },
                    }}
                />
            </Box>

            {/* The running total of a sum, the rounding, and the two checks.
                Only ever shown when there is something to say, so the row does
                not jump about as somebody types. */}
            {(focused || impossible || odd || rounded
              || reading.state === STATE.INVALID) && (
                <Box sx={{ px: 2, pb: 1, mt: -0.5 }}>
                    {reading.state === STATE.INCOMPLETE && reading.value !== null && (
                        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                            = {groupDigits(reading.value)} {item.measurement_unit} so far
                        </Typography>
                    )}
                    {reading.state === STATE.OK && /[+\-*/]/.test(raw || '') && (
                        <Typography variant="caption" sx={{ color: tokens.good }}>
                            = {groupDigits(value)} {item.measurement_unit}
                        </Typography>
                    )}
                    {active.factor !== 1 && value !== null && (
                        <Typography variant="caption" sx={{
                            display: 'block', color: tokens.good, fontWeight: 600 }}>
                            = {groupDigits(value)} {item.measurement_unit}
                        </Typography>
                    )}
                    {rounded && (
                        <Typography variant="caption" sx={{
                            display: 'block', color: tokens.textSecondary }}>
                            Saved as {groupDigits(value)} {item.measurement_unit}
                        </Typography>
                    )}
                    {reading.state === STATE.INVALID && (
                        <Typography variant="caption" sx={{ color: tokens.critical }}>
                            {reading.message}
                        </Typography>
                    )}
                    {impossible && (
                        <Typography variant="caption" sx={{
                            display: 'block', color: tokens.critical, fontWeight: 600 }}>
                            {impossible}
                        </Typography>
                    )}
                    {odd && (
                        // Amber, never red: this one does not block. Stock does
                        // jump when a delivery lands, and a screen that argues
                        // with somebody holding the actual sack is a screen they
                        // learn to ignore.
                        <Typography variant="caption" sx={{
                            display: 'block', color: 'warning.main' }}>
                            {odd.kind === 'high' ? 'Much more' : 'Much less'} than the
                            last count of {groupDigits(odd.previous)} {item.measurement_unit}.
                            Send it if that is right.
                        </Typography>
                    )}
                    {focused && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {/* A numeric keypad has no + or ×, and adding up
                                part-full containers is the whole point of
                                accepting a sum. */}
                            {['+', '*'].map((operator) => (
                                <Chip key={operator} size="small" variant="outlined"
                                    label={operator === '*' ? '×' : operator}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => onChange(item.id, `${raw || ''}${operator}`)} />
                            ))}
                            <Chip size="small" variant="outlined" label="000"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => onChange(item.id, `${raw || ''}000`)} />
                        </Box>
                    )}
                </Box>
            )}
            {!isLast && <Divider />}
        </>
    );
}

const MemoRow = React.memo(CountRow);

function AddInventory(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const navigate = useNavigate();
    const username = UserProfile.getName();

    const [items, setItems] = useState(null);
    const [entries, setEntries] = useState({});
    const [units, setUnits] = useState({});
    // The counter is the default: this screen is a walk past shelves, done
    // daily by the same couple of people. The list stays one tap away for
    // finding one item, correcting something, or seeing the whole thing.
    const [view, setView] = useState('count');
    const [cursor, setCursor] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [sending, setSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [hideCounted, setHideCounted] = useState(false);
    const [pendingDraft, setPendingDraft] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [saveFailed, setSaveFailed] = useState(false);
    const [, setTick] = useState(0);

    const entriesRef = useRef(entries);
    entriesRef.current = entries;
    const unitsRef = useRef(units);
    unitsRef.current = units;
    const writeTimer = useRef(null);

    // -- the draft -------------------------------------------------------

    const flush = useCallback(() => {
        const ok = saveDraft(username, entriesRef.current, unitsRef.current);
        setSaveFailed(!ok);
        if (ok) setSavedAt(new Date());
    }, [username]);

    useEffect(() => {
        // "Moved away from the page" on a phone means the screen locked, a call
        // came in, or they switched apps. Those fire visibilitychange and
        // pagehide; beforeunload is unreliable on mobile and is the wrong event
        // to trust with somebody's work.
        const onHide = () => {
            if (document.visibilityState === 'hidden') flush();
        };
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', flush);
        return () => {
            document.removeEventListener('visibilitychange', onHide);
            window.removeEventListener('pagehide', flush);
            if (writeTimer.current) clearTimeout(writeTimer.current);
        };
    }, [flush]);

    // Keep "saved 3 minutes ago" honest without re-rendering the list.
    useEffect(() => {
        const timer = setInterval(() => setTick((n) => n + 1), 30000);
        return () => clearInterval(timer);
    }, []);

    const updateEntry = useCallback((id, raw) => {
        setEntries((prev) => {
            const next = { ...prev };
            // `raw` may be a function of the current value, so a burst of
            // keypresses inside one frame each build on the last.
            const value = typeof raw === 'function' ? raw(prev[id] || '') : raw;
            if (value === '' || value === null || value === undefined) delete next[id];
            else next[id] = value;
            entriesRef.current = next;
            return next;
        });
        if (writeTimer.current) clearTimeout(writeTimer.current);
        writeTimer.current = setTimeout(flush, 400);
    }, [flush]);

    const updateUnit = useCallback((id, label) => {
        setUnits((prev) => {
            const next = { ...prev, [id]: label };
            unitsRef.current = next;
            return next;
        });
        // Immediate, not debounced: the unit changes what the typed number
        // means, so it must never be the thing left unsaved.
        setTimeout(flush, 0);
    }, [flush]);

    const move = useCallback((step) => {
        setCursor((at) => Math.min(Math.max(at + step, 0), (items || []).length - 1));
    }, [items]);

    // -- loading ---------------------------------------------------------

    const load = useCallback(() => {
        setLoading(true);
        setLoadFailed(false);
        dropLegacyCookie();
        api.get('/pos/inventory/list/non_perishable/').then((res) => {
            const rows = [...(res.data.inventory_items || [])].sort(
                // A real comparator. The previous one returned 1 for equal
                // values, which is not a total order, so items sharing a
                // display_index could swap places between loads - and the list
                // order is the route round the kitchen.
                (a, b) => (a.display_index ?? 0) - (b.display_index ?? 0));
            setItems(rows);

            const draft = loadDraft(username);
            const live = Object.fromEntries(
                Object.entries(draft.entries || {})
                    .filter(([id]) => rows.some((row) => String(row.id) === String(id))));
            const liveUnits = Object.fromEntries(
                Object.entries(draft.units || {})
                    .filter(([id]) => rows.some((row) => String(row.id) === String(id))));
            if (Object.keys(live).length) {
                // Never applied silently. Somebody submitting yesterday's
                // numbers without noticing is a worse failure than retyping.
                setPendingDraft({ entries: live, units: liveUnits,
                                  savedAt: draft.savedAt, date: draft.date,
                                  count: Object.keys(live).length });
            }
        }).catch((error) => {
            setLoadFailed(true);
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setLoading(false));
    }, [username, showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    // -- derived ---------------------------------------------------------

    const counted = useMemo(() => Object.keys(entries)
        .filter((id) => read(entries[id]).state === STATE.OK).length, [entries]);
    const blocked = useMemo(() => (items || []).filter((item) => {
        const reading = read(entries[item.id]);
        if (reading.state === STATE.INVALID) return true;
        if (reading.state !== STATE.OK) return false;
        const choices = unitChoices(item.measurement_unit);
        const active = choices.find((u) => u.label === units[item.id]) || choices[0];
        return Boolean(isImpossible(Math.round(reading.value * active.factor),
                                    item.measurement_unit));
    }), [items, entries, units]);

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return (items || []).filter((item) => {
            if (hideCounted && read(entries[item.id]).state === STATE.OK) return false;
            if (needle && !item.item_name.toLowerCase().includes(needle)) return false;
            return true;
        });
    }, [items, entries, search, hideCounted]);

    // -- sending ---------------------------------------------------------

    const send = () => {
        setConfirmOpen(false);
        setSending(true);
        // Everything is converted to the unit the item is stored in before it
        // leaves, so the server receives one consistent unit whichever way the
        // person chose to count.
        const payload = (items || [])
            .map((item) => {
                const reading = read(entries[item.id]);
                if (reading.state !== STATE.OK) return null;
                const choices = unitChoices(item.measurement_unit);
                const active = choices.find((u) => u.label === units[item.id]) || choices[0];
                return {
                    id: item.id,
                    value: Math.round(reading.value * active.factor),
                    measurement_unit: item.measurement_unit,
                };
            })
            .filter(Boolean);

        api.post('/pos/inventory/update/', payload).then(() => {
            clearDraft(username);
            setEntries({});
            entriesRef.current = {};
            setUnits({});
            unitsRef.current = {};
            showSuccessMessage(`${payload.length} item${payload.length === 1 ? '' : 's'} counted`);
            // The stock view, not this list. Reloading this one after a full
            // count renders "nothing pending", which reads as though the work
            // vanished; the stock view is the receipt, in the same order.
            navigate('/inventory/view');
        }).catch((error) => {
            // The draft is deliberately untouched, so nothing is lost and the
            // same Send can simply be pressed again.
            showErrorMessage(`${getErrorMessage(error)} - nothing was sent, and `
                + 'your numbers are still saved on this phone.');
        }).finally(() => setSending(false));
    };

    const showSearch = (items || []).length >= SEARCH_MIN_ITEMS;
    const total = (items || []).length;

    return (
        <AccessController onlyAdmin={false}>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}
                fullWidth maxWidth="xs">
                <DialogTitle>Send {counted} count{counted === 1 ? '' : 's'}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {total - counted > 0 ? (
                            <>The {total - counted} item{total - counted === 1 ? '' : 's'} you
                            have left blank will not be changed. You can count them
                            later and send again.</>
                        ) : (
                            <>Every item has a number. This replaces the recorded
                            stock for all {counted} of them.</>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={send}>Send</Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ maxWidth: 760, mx: 'auto', pb: 11 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Stock count</Typography>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                        {loading ? 'Loading today\'s list' : `${username} · ${total} items today`}
                    </Typography>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && loadFailed && (
                    <Alert severity="error" action={
                        <Button color="inherit" size="small" onClick={load}>Retry</Button>
                    }>
                        <AlertTitle>Could not load today&rsquo;s list</AlertTitle>
                        Nothing has been lost. Try again when you have a signal.
                    </Alert>
                )}

                {!loading && !loadFailed && total === 0 && (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" gutterBottom>Nothing to count today</Typography>
                        <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                            Every item is up to date. Come back tomorrow.
                        </Typography>
                        <Button sx={{ mt: 2 }} onClick={() => navigate('/inventory/view')}>
                            View stock
                        </Button>
                    </Paper>
                )}

                {!loading && !loadFailed && total > 0 && (
                    <>
                        {pendingDraft && (
                            <Alert severity="info" sx={{ mb: 2 }}
                                action={(
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Button color="inherit" size="small"
                                            onClick={() => {
                                                clearDraft(username);
                                                setPendingDraft(null);
                                            }}>
                                            Discard
                                        </Button>
                                        <Button color="inherit" size="small" variant="outlined"
                                            onClick={() => {
                                                setEntries(pendingDraft.entries);
                                                entriesRef.current = pendingDraft.entries;
                                                setUnits(pendingDraft.units || {});
                                                unitsRef.current = pendingDraft.units || {};
                                                setSavedAt(pendingDraft.savedAt);
                                                setPendingDraft(null);
                                                // Land on the first item still
                                                // blank - the shelf they were
                                                // standing at when interrupted.
                                                const at = (items || []).findIndex(
                                                    (row) => !pendingDraft.entries[row.id]);
                                                setCursor(at === -1 ? 0 : at);
                                            }}>
                                            Continue
                                        </Button>
                                    </Box>
                                )}>
                                <AlertTitle>
                                    Unfinished count &mdash; {pendingDraft.count} of {total} filled
                                </AlertTitle>
                                Saved {savedAgo(pendingDraft.savedAt)}
                                {pendingDraft.date && pendingDraft.date !== businessDate()
                                    ? ` — from ${pendingDraft.date}, not today` : ''}.
                            </Alert>
                        )}

                        {!STORAGE_AVAILABLE && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                This browser will not save a draft. Finish the count
                                without leaving the page, or send what you have as you go.
                            </Alert>
                        )}
                        {saveFailed && STORAGE_AVAILABLE && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Could not save your draft on this phone. Send what you
                                have so far so it is not lost.
                            </Alert>
                        )}

                        {/* Progress, and the reassurance that it is saved. */}
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                alignItems: 'baseline', gap: 1, mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {counted} counted · {total - counted} left
                                </Typography>
                                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                    {savedAt ? `saved ${savedAgo(savedAt)}` : 'not saved yet'}
                                </Typography>
                            </Box>
                            <LinearProgress variant="determinate"
                                value={total ? (counted / total) * 100 : 0}
                                sx={{ height: 6, borderRadius: 3 }} />
                        </Paper>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center',
                            mb: 1.5, flexWrap: 'wrap' }}>
                            {/* One tap between the two ways of working. Which
                                one you are in is remembered for the session. */}
                            <Button
                                size="small" variant="outlined"
                                startIcon={view === 'count' ? <ViewListIcon /> : <KeyboardIcon />}
                                onClick={() => setView(view === 'count' ? 'list' : 'count')}>
                                {view === 'count' ? 'List' : 'Counter'}
                            </Button>
                            {view === 'list' && showSearch && (
                                <TextField size="small" placeholder="Find an item"
                                    value={search} onChange={(e) => setSearch(e.target.value)}
                                    sx={{ flexGrow: 1, minWidth: 160 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: search ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" aria-label="Clear search"
                                                    onClick={() => setSearch('')}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    }} />
                            )}
                            {/* The one control that makes 104 items tractable:
                                it turns the list into exactly what is left. */}
                            {view === 'list' && (
                                <FormControlLabel
                                    control={<Switch checked={hideCounted} size="small"
                                        onChange={(e) => setHideCounted(e.target.checked)} />}
                                    label={<Typography variant="body2">Hide counted</Typography>} />
                            )}
                        </Box>

                        {view === 'count' ? (
                            <Paper variant="outlined" sx={{
                                overflow: 'hidden', display: 'flex',
                                flexDirection: 'column', minHeight: 430,
                            }}>
                                <CountStage
                                    item={items[Math.min(cursor, items.length - 1)]}
                                    index={Math.min(cursor, items.length - 1)}
                                    total={items.length}
                                    raw={entries[items[Math.min(cursor, items.length - 1)].id] || ''}
                                    unit={units[items[Math.min(cursor, items.length - 1)].id]}
                                    previous={cursor > 0 ? (() => {
                                        const before = items[cursor - 1];
                                        const reading = read(entries[before.id]);
                                        const choices = unitChoices(before.measurement_unit);
                                        const active = choices.find(
                                            (u) => u.label === units[before.id]) || choices[0];
                                        return {
                                            item: before,
                                            stored: reading.state === STATE.OK
                                                ? Math.round(reading.value * active.factor) : null,
                                        };
                                    })() : null}
                                    next={cursor + 1 < items.length ? items[cursor + 1] : null}
                                    onChange={(raw) => updateEntry(
                                        items[Math.min(cursor, items.length - 1)].id, raw)}
                                    onUnit={(label) => updateUnit(
                                        items[Math.min(cursor, items.length - 1)].id, label)}
                                    onMove={move}
                                    onDone={() => setView('list')}
                                />
                            </Paper>
                        ) : (
                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                            {visible.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                                        {hideCounted && counted === total
                                            ? 'Everything is counted. Send it below.'
                                            : 'No item matches that name.'}
                                    </Typography>
                                </Box>
                            ) : visible.map((item, index) => (
                                <MemoRow
                                    key={item.id}
                                    item={item}
                                    raw={entries[item.id] || ''}
                                    unit={units[item.id]}
                                    onChange={updateEntry}
                                    onUnit={updateUnit}
                                    tokens={tokens}
                                    isLast={index === visible.length - 1}
                                />
                            ))}
                        </Paper>
                        )}
                    </>
                )}
            </Box>

            {/* Reachable from anywhere in the list, not marooned under 104 rows. */}
            {!loading && !loadFailed && total > 0 && (
                <Paper elevation={8} sx={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10,
                    px: 2, py: 1.5, borderRadius: 0,
                }}>
                    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
                        <Button variant="contained" fullWidth
                            disabled={sending || counted === 0 || blocked.length > 0}
                            onClick={() => setConfirmOpen(true)}>
                            {sending ? 'Sending...'
                                : counted === 0 ? 'Nothing counted yet'
                                    : `Send ${counted} count${counted === 1 ? '' : 's'}`}
                        </Button>
                        <Typography variant="caption" sx={{
                            display: 'block', textAlign: 'center', mt: 0.5,
                            color: blocked.length ? tokens.critical : tokens.textMuted }}>
                            {blocked.length
                                ? `Fix ${blocked.length} item${blocked.length === 1 ? '' : 's'}: `
                                  + blocked.slice(0, 2).map((i) => i.item_name).join(', ')
                                  + (blocked.length > 2 ? '...' : '')
                                : 'Items left blank will not be changed. You can send more later.'}
                        </Typography>
                    </Box>
                </Paper>
            )}
        </AccessController>
    );
}

export default AddInventory;
